import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights.service';
import { GlobalErrorHandler } from '../../services/global-error-handler';
import { IsBusyService } from '../../services/is-busy/is-busy.service';
import { UnitOfWorkService } from '../../services/unit-of-work/unit-of-work.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import * as model from '../../../model/model';
import * as modelDto from '../../../model/modelDto';

const defaultLimit = 15;
const loadingKey = 'admin:inspectionQuestions:loadData';
const savingKey = 'admin:inspectionQuestions:saveActive';

@Component({
    selector: 'app-inspection-questions',
    templateUrl: './inspection-questions.component.html',
    standalone: false
})
export class InspectionQuestionsComponent implements OnInit {
    questions: model.InspectionQuestion[] = [];
    page = 1;
    limit = defaultLimit;
    pageCount = 1;
    rowCount = 0;
    pageLimitOptions = [15, 25, 50, 100];
    isToggling = false;
    filters: modelDto.GridFilter[] = [];
    questionCodeFilter: string = null;
    questionTextFilter: string = null;
    lastModifiedByFilter: string = null;
    lastModifiedFromFilter: string = null;
    lastModifiedToFilter: string = null;
    activeFilter = false;
    draftQuestionCodeFilter: string = null;
    draftQuestionTextFilter: string = null;
    draftLastModifiedByFilter: string = null;
    draftLastModifiedFromFilter: string = null;
    draftLastModifiedToFilter: string = null;
    draftActiveFilter = false;
    lastModifiedDateFilterError = '';

    constructor(
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        private activeRoute: ActivatedRoute,
        private router: Router,
        public isBusy: IsBusyService
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.activeRoute.queryParams.subscribe(routeParams => {
            const parsedPage = Number.parseInt(routeParams.page, 10);
            const parsedLimit = Number.parseInt(routeParams.limit, 10);

            this.page = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;
            this.limit = this.pageLimitOptions.includes(parsedLimit) ? parsedLimit : defaultLimit;
            this.updateDataGrid();
        });
    }

    async updateDataGrid() {
        this.isBusy.set(loadingKey, true, 'Loading questions');
        try {
            const [countResult, questions] = await Promise.all([
                this.uow.getInspectionQuestionsCount(JSON.stringify(this.filters)),
                this.uow.getInspectionQuestions(this.page, this.limit, JSON.stringify(this.filters))
            ]);

            this.rowCount = countResult[0] || 0;
            this.pageCount = Math.max(1, Math.ceil(this.rowCount / this.limit));
            this.questions = questions;
        } catch (reason) {
            this.errorHandler.handleError(reason);
        } finally {
            this.isBusy.set(loadingKey, false);
        }
    }

    onFilterOpen(isOpen: boolean) {
        if (isOpen) {
            this.draftQuestionCodeFilter = this.questionCodeFilter;
            this.draftQuestionTextFilter = this.questionTextFilter;
            this.draftLastModifiedByFilter = this.lastModifiedByFilter;
            this.draftLastModifiedFromFilter = this.lastModifiedFromFilter;
            this.draftLastModifiedToFilter = this.lastModifiedToFilter;
            this.draftActiveFilter = this.activeFilter;
        }
    }

    applyFilters(dropdown?: NgbDropdown) {
        if (!this.lastModifiedDateRangeIsValid()) {
            return;
        }

        this.questionCodeFilter = this.draftQuestionCodeFilter;
        this.questionTextFilter = this.draftQuestionTextFilter;
        this.lastModifiedByFilter = this.draftLastModifiedByFilter;
        this.lastModifiedFromFilter = this.draftLastModifiedFromFilter;
        this.lastModifiedToFilter = this.draftLastModifiedToFilter;
        this.activeFilter = this.draftActiveFilter;
        this.filters = [];

        if (this.questionCodeFilter?.trim()) {
            this.filters.push({ key: 'questionCode', value: this.questionCodeFilter } as modelDto.GridFilter);
        }
        if (this.questionTextFilter?.trim()) {
            this.filters.push({ key: 'questionText', value: this.questionTextFilter } as modelDto.GridFilter);
        }
        if (this.lastModifiedByFilter?.trim()) {
            this.filters.push({ key: 'modifiedBy', value: this.lastModifiedByFilter } as modelDto.GridFilter);
        }
        if (this.lastModifiedFromFilter) {
            this.filters.push({
                key: 'modifiedFromUtc',
                value: this.toUtcDateFilterValue(this.lastModifiedFromFilter)
            } as modelDto.GridFilter);
        }
        if (this.lastModifiedToFilter) {
            this.filters.push({
                key: 'modifiedToUtc',
                value: this.toUtcDateFilterValue(this.lastModifiedToFilter, true)
            } as modelDto.GridFilter);
        }
        if (this.activeFilter) {
            this.filters.push({ key: 'active', value: 'true' } as modelDto.GridFilter);
        }

        this.page = 1;
        this.updateDataGrid();
        dropdown?.close();
    }

    clearFilters(dropdown?: NgbDropdown) {
        this.filters = [];
        this.questionCodeFilter = null;
        this.questionTextFilter = null;
        this.lastModifiedByFilter = null;
        this.lastModifiedFromFilter = null;
        this.lastModifiedToFilter = null;
        this.activeFilter = false;
        this.draftQuestionCodeFilter = null;
        this.draftQuestionTextFilter = null;
        this.draftLastModifiedByFilter = null;
        this.draftLastModifiedFromFilter = null;
        this.draftLastModifiedToFilter = null;
        this.draftActiveFilter = false;
        this.lastModifiedDateFilterError = '';
        this.page = 1;
        this.updateDataGrid();
        dropdown?.close();
    }

    private lastModifiedDateRangeIsValid() {
        const from = this.draftLastModifiedFromFilter;
        const to = this.draftLastModifiedToFilter;

        if (from && !this.isValidDateFilterValue(from)) {
            this.lastModifiedDateFilterError = 'Last Modified From must be a valid date.';
            return false;
        }

        if (to && !this.isValidDateFilterValue(to)) {
            this.lastModifiedDateFilterError = 'Last Modified To must be a valid date.';
            return false;
        }

        if (from && to && from > to) {
            this.lastModifiedDateFilterError = 'Last Modified From must be before or equal to Last Modified To.';
            return false;
        }

        this.lastModifiedDateFilterError = '';
        return true;
    }

    private isValidDateFilterValue(value: string) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return false;
        }

        const date = new Date(`${value}T00:00:00`);
        return !Number.isNaN(date.getTime())
            && date.getFullYear() === Number(value.substring(0, 4))
            && date.getMonth() + 1 === Number(value.substring(5, 7))
            && date.getDate() === Number(value.substring(8, 10));
    }

    private toUtcDateFilterValue(value: string, endExclusive = false) {
        const date = new Date(`${value}T00:00:00`);
        if (endExclusive) {
            date.setDate(date.getDate() + 1);
        }

        return date.toISOString();
    }

    getDisplayText(question: model.InspectionQuestion) {
        const text = question.questionText || '';
        return text.length > 40 ? `${text.substring(0, 40)}...` : text;
    }

    getLastModifiedDate(question: model.InspectionQuestion) {
        return question.modifiedDate || question.createdDate;
    }

    getLastModifiedBy(question: model.InspectionQuestion) {
        const employee = question.modifiedByUser || question.createdByUser;
        if (!employee) {
            return '---';
        }

        if (employee.fullName && employee.fullName.trim()) {
            return employee.fullName;
        }

        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        return fullName || employee.id;
    }

    editQuestion(question: model.InspectionQuestion) {
        if (this.isToggling) {
            return;
        }

        this.router.navigate(['../question'], {
            relativeTo: this.activeRoute,
            queryParams: { id: question.id }
        });
    }

    async toggleActive(question: model.InspectionQuestion) {
        if (this.isToggling) {
            return;
        }

        const previousValue = !question.isActive;
        this.isToggling = true;
        this.isBusy.set(savingKey, true, 'Updating question');

        try {
            await this.uow.inspectionQuestions.saveChanges([question]);
            if (!question.entityAspect.entityState.isUnchanged()) {
                question.entityAspect.rejectChanges();
                question.isActive = previousValue;
                this.errorHandler.handleError(new Error('The question active state could not be saved.'));
                return;
            }

            await this.updateDataGrid();
        } catch (reason) {
            question.entityAspect.rejectChanges();
            question.isActive = previousValue;
            this.errorHandler.handleError(reason);
        } finally {
            this.isToggling = false;
            this.isBusy.set(savingKey, false);
        }
    }
}
